import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi, POLL_MS } from "@/lib/standup-api";
import { Button } from "@/components/ui/button";
import { Play, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Frame 2.1 — Launch loader.
//
// Phase 3 ships a placeholder: gradient background, typography, simulated
// 5-second warm-up bar, "Start Meeting" CTA. The actual font-morph animation
// + audio sting drops in Phase 5 once the user sends the assets + code.

const TAGLINES = [
  "BLOCKERS WELCOME", "CAFFIENE OPTIONAL", "MIC CHECK", "BLAME THE CODE",
  "UNBLOCK THY NEIGHBOR", "SPILL THE TEA", "CHEER ASIDES ONLY",
  "PROGRESS NOT PERFECTION",
];

export default function StandUpLaunch() {
  const { sessionId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  // Drive the warm-up bar to 100% over ~5s.
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(100, (elapsed / 5000) * 100));
      if (elapsed >= 5000) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user) return;
    standupApi.progress(sessionId).then((r) => setSession(r.data)).catch(() => {});
  }, [user, sessionId]);

  async function startMeeting() {
    try {
      await standupApi.startSession(sessionId);
    } catch (_) { /* host-only — non-host just routes */ }
    // Hosts go to host view; participants to participate view
    const isHost = session?.status && user?.email; // we can't know reliably here without a server check
    // Cheap heuristic: try the host endpoint; if 403, fall back.
    navigate(`/standup/session/${sessionId}/route`);
  }

  if (authLoading || !user) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[linear-gradient(135deg,#BFE0FF_0%,#DCC9F3_45%,#FFB6CA_100%)] text-slate-900 relative overflow-hidden" data-testid="standup-launch">
      <div className="absolute top-6 left-8 flex items-center gap-2 text-xs font-semibold tracking-widest">
        <span className="h-2 w-2 rounded-full bg-rose-500" />
        LIVE · DAILY STANDUP
      </div>
      <div className="absolute top-6 right-8 text-xs font-semibold tracking-widest text-slate-700">
        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }).toUpperCase()}
      </div>

      <div className="grid place-items-center min-h-screen px-6">
        <div className="text-center">
          <h1 className="text-[88px] sm:text-[120px] leading-[0.95] font-black tracking-tight">
            <span className="block">StandUp</span>
            <span className="block" style={{
              color: "transparent",
              WebkitTextStroke: "2px #0f172a",
            }}>Launching…</span>
          </h1>

          <div className="mt-10 inline-flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-1 text-slate-700">
              {[3, 5, 8, 5, 3].map((h, i) => (
                <span key={i} className="w-1 bg-slate-700 rounded-full animate-pulse" style={{ height: `${h * 4}px`, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
            <div className="text-[11px] font-bold tracking-widest text-slate-600">WARMING UP THE ROOM</div>
          </div>

          <div className="mt-10 w-[420px] max-w-full">
            <div className="flex items-center justify-between text-[10px] font-bold tracking-widest text-slate-600 mb-1">
              <span>STARTS IN</span>
              <span>{Math.max(0, Math.ceil((100 - progress) / 20))}s</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/40 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 via-amber-300 to-pink-300 transition-[width] duration-150" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <Button onClick={startMeeting} className="mt-8 h-10 bg-slate-950 hover:bg-slate-800 text-white text-xs font-semibold" data-testid="start-meeting-btn">
            <Play size={11} weight="fill" className="mr-1" /> Start Meeting
          </Button>
        </div>
      </div>

      {/* tagline ticker bottom */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden py-3 border-t border-white/40 bg-white/10 backdrop-blur-sm">
        <div className="flex gap-10 text-[10px] font-bold tracking-widest text-slate-700 whitespace-nowrap justify-around">
          {TAGLINES.map((t) => <span key={t}>◆ {t}</span>)}
        </div>
      </div>
    </div>
  );
}
